import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Cashier } from '../../types';
import {
    cachedCashiers, cashierLabel, filterCashiers, loadCashiers, matchRosterEntry, phoneDigits,
} from './cashierPickerUtils';

// Picks a cashier from the roster (backend routers/cashiers.py) and fills BOTH
// the name and the phone on the payment form. Modelled on BankNameInput, which
// does the same job for a transaction's bankName -- including the "Other"
// escape hatch, so a cash payment can still be recorded for someone who is not
// on the roster yet. The payment stores cashierName/cashierPhone exactly as it
// always has; the roster only decides what gets typed into them.
//
// Only ACTIVE cashiers are offered: a deactivated one must not be selectable
// for a new collection, while the payments already naming them stay readable.

interface CashierPickerProps {
    name: string;
    phone: string;
    onChange: (next: { cashierName: string; cashierPhone: string }) => void;
    className?: string;
    placeholder?: string;
    disabled?: boolean;
}

const OTHER = '__OTHER__';

const CashierPicker: React.FC<CashierPickerProps> = ({
    name,
    phone,
    onChange,
    className = '',
    placeholder = 'Search cashier name or number',
    disabled = false,
}) => {
    const [cashiers, setCashiers] = useState<Cashier[]>(cachedCashiers());
    const [open, setOpen] = useState(false);
    const [isOther, setIsOther] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const otherInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => { loadCashiers().then(setCashiers); }, []);

    // An existing payment being edited opens in free-text mode when its cashier
    // is not on the roster -- showing it as a search box would invite a click
    // that silently replaces a name somebody already recorded.
    useEffect(() => {
        if (name && cashiers.length && !matchRosterEntry(cashiers, name, phone)) {
            setIsOther(true);
        }
        // Runs on the roster arriving, not on every keystroke.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cashiers.length]);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    useEffect(() => {
        if (isOther) otherInputRef.current?.focus();
    }, [isOther]);

    const selected = matchRosterEntry(cashiers, name, phone);
    const filtered = filterCashiers(cashiers, name);

    const handleSelect = (cashier: Cashier | typeof OTHER) => {
        if (cashier === OTHER) {
            setIsOther(true);
            onChange({ cashierName: '', cashierPhone: '' });
            setOpen(false);
            return;
        }
        // The roster's own spelling and number, so the payment and the roster
        // agree -- the whole point of having a list.
        onChange({
            cashierName: cashier.cashierName,
            // Trimmed to the 10-digit subscriber number: the payment forms
            // validate exactly 10 digits, and a roster row may carry a country code.
            cashierPhone: phoneDigits(cashier.cashierPhoneNumber).slice(-10),
        });
        setOpen(false);
    };

    // "Other" free-text mode — what every cash payment did before the roster.
    if (isOther) {
        return (
            <div ref={containerRef} className="relative">
                <input
                    ref={otherInputRef}
                    type="text"
                    value={name}
                    onChange={e => onChange({
                        cashierName: e.target.value.replace(/[^a-zA-Z ]/g, ''),
                        cashierPhone: phone,
                    })}
                    placeholder="Enter cashier name"
                    className={className}
                    autoComplete="off"
                    readOnly={disabled}
                />
                {!disabled && (
                    <button
                        type="button"
                        onMouseDown={() => {
                            setIsOther(false);
                            onChange({ cashierName: '', cashierPhone: '' });
                        }}
                        title="Back to roster"
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                    >
                        <X size={13} />
                    </button>
                )}
            </div>
        );
    }

    return (
        <div ref={containerRef} className="relative">
            <div className="relative">
                <input
                    type="text"
                    value={name}
                    onChange={e => {
                        // Typing searches the roster. The phone is cleared with
                        // it, so a half-edited name can never be left paired
                        // with the previous cashier's number.
                        onChange({ cashierName: e.target.value, cashierPhone: '' });
                        setOpen(true);
                    }}
                    onFocus={() => !disabled && setOpen(true)}
                    onKeyDown={e => { if (e.key === 'Escape') setOpen(false); }}
                    placeholder={placeholder}
                    className={className}
                    autoComplete="off"
                    readOnly={disabled}
                />
                <ChevronDown
                    size={14}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
                />
            </div>

            {open && !disabled && (
                <ul className="absolute z-50 top-full left-0 right-0 mt-1 max-h-48 overflow-y-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                    {filtered.map(cashier => (
                        <li
                            key={cashier.cashierId}
                            onMouseDown={() => handleSelect(cashier)}
                            className={`px-3 py-2 text-sm cursor-pointer transition-colors hover:bg-[#1B4332]/10 hover:text-[#1B4332] ${selected?.cashierId === cashier.cashierId ? 'bg-[#1B4332]/10 text-[#1B4332] font-semibold' : 'text-slate-700'}`}
                        >
                            {cashierLabel(cashier)}
                        </li>
                    ))}
                    {filtered.length === 0 && (
                        <li className="px-3 py-2 text-sm text-slate-400 italic">
                            {cashiers.length === 0 ? 'No cashiers on the roster yet' : 'No match'}
                        </li>
                    )}
                    <li
                        onMouseDown={() => handleSelect(OTHER)}
                        className="px-3 py-2 text-sm cursor-pointer transition-colors border-t border-slate-100 mt-1 text-slate-500 hover:bg-slate-50 hover:text-slate-700 italic"
                    >
                        Other (enter manually)
                    </li>
                </ul>
            )}
        </div>
    );
};

export default CashierPicker;
